import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, Loader2, X } from "lucide-react";
import {
  api,
  getToken,
  setToken,
  clearToken,
  formatApiErrorDetail,
  resolveImg,
} from "../lib/api";
import { DEFAULT_CONTENT } from "../lib/defaults";

function LoginForm({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setToken(data.token);
      onLogin(data.user);
      toast.success("Welcome back, Saivi");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-cream px-6 py-16">
      <form
        data-testid="admin-login-form"
        onSubmit={submit}
        className="w-full max-w-sm border border-linen bg-white p-8 shadow-[0_30px_60px_-30px_rgba(24,19,16,0.3)]"
      >
        <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-vermilion">Owner Studio</p>
        <h1 className="mt-3 font-serif text-3xl tracking-tight text-ink">
          Artwith<span className="italic text-vermilion">.saivi</span>
        </h1>
        <p className="mt-2 text-sm text-clay">Sign in to manage artworks, prices and site text.</p>
        <label className="mt-8 block">
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-sand">Email</span>
          <input
            data-testid="admin-login-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1.5 w-full border border-linen bg-cream px-4 py-3 text-sm text-ink outline-none transition-colors focus:border-vermilion"
          />
        </label>
        <label className="mt-5 block">
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-sand">Password</span>
          <input
            data-testid="admin-login-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-1.5 w-full border border-linen bg-cream px-4 py-3 text-sm text-ink outline-none transition-colors focus:border-vermilion"
          />
        </label>
        <button
          data-testid="admin-login-submit-button"
          disabled={busy}
          className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-umber py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] text-cream transition-colors hover:bg-vermilion disabled:opacity-60"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          Sign In
        </button>
      </form>
    </div>
  );
}

const EMPTY_ART = { title: "", medium: "Oil on Canvas", size: "", year: "2026", price: "", story: "", image: "" };

function ArtworkForm({ initial, onClose, onSaved }) {
  const [f, setF] = useState(initial ? { ...EMPTY_ART, ...initial } : EMPTY_ART);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(initial?.image ? resolveImg(initial.image) : "");
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const pickFile = (e) => {
    const picked = e.target.files?.[0];
    if (!picked) return;
    setFile(picked);
    setPreview(URL.createObjectURL(picked));
  };

  const save = async () => {
    if (!f.title.trim()) return toast.error("Title is required");
    if (!file && !f.image) return toast.error("Please choose an image");
    setBusy(true);
    try {
      let image = f.image || "";
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const up = await api.post("/admin/upload", fd);
        image = up.data.url;
      }
      const payload = {
        title: f.title,
        medium: f.medium,
        size: f.size,
        year: f.year,
        price: f.price,
        story: f.story,
        image,
        sold: !!f.sold,
      };
      if (initial?.id) await api.put(`/artworks/${initial.id}`, payload);
      else await api.post("/artworks", payload);
      toast.success(initial ? "Artwork updated" : "Artwork added");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const field = "mt-1.5 w-full border border-linen bg-cream px-4 py-3 text-sm text-ink outline-none transition-colors focus:border-vermilion";
  const label = "font-mono text-[10px] uppercase tracking-[0.22em] text-sand";

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-umber/70 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        data-testid="admin-artwork-form"
        onClick={(e) => e.stopPropagation()}
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto border border-linen bg-cream p-8"
      >
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-2xl tracking-tight text-ink">
            {initial ? "Edit artwork" : "Add artwork"}
          </h2>
          <button onClick={onClose} aria-label="Close" className="rounded-full border border-linen p-2 text-clay hover:border-vermilion hover:text-vermilion">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4">
          <label className="col-span-2 block">
            <span className={label}>Title</span>
            <input data-testid="admin-artwork-title-input" value={f.title} onChange={set("title")} className={field} />
          </label>
          <label className="block">
            <span className={label}>Medium</span>
            <input data-testid="admin-artwork-medium-input" value={f.medium} onChange={set("medium")} className={field} />
          </label>
          <label className="block">
            <span className={label}>Size</span>
            <input data-testid="admin-artwork-size-input" placeholder={'e.g. 24" x 32"'} value={f.size} onChange={set("size")} className={field} />
          </label>
          <label className="block">
            <span className={label}>Year</span>
            <input data-testid="admin-artwork-year-input" value={f.year} onChange={set("year")} className={field} />
          </label>
          <label className="block">
            <span className={label}>Price</span>
            <input data-testid="admin-artwork-price-input" placeholder="₹95,000 (India)" value={f.price} onChange={set("price")} className={field} />
          </label>
          <label className="col-span-2 block">
            <span className={label}>Story</span>
            <textarea data-testid="admin-artwork-story-input" rows={4} value={f.story} onChange={set("story")} className={field} />
          </label>
          <label className="col-span-2 block">
            <span className={label}>Painting image</span>
            <input
              data-testid="admin-artwork-image-input"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={pickFile}
              className="mt-1.5 block w-full text-sm text-clay file:mr-4 file:rounded-full file:border-0 file:bg-umber file:px-5 file:py-2.5 file:font-mono file:text-[10px] file:uppercase file:tracking-[0.2em] file:text-cream hover:file:bg-vermilion"
            />
          </label>
          {preview && (
            <div className="col-span-2">
              <img src={preview} alt="preview" className="mx-auto max-h-44 border border-linen bg-white p-2" />
            </div>
          )}
        </div>

        <label
          data-testid="admin-artwork-sold-toggle"
          className="mt-5 flex cursor-pointer items-center gap-3"
        >
          <input
            type="checkbox"
            checked={!!f.sold}
            onChange={(e) => setF((p) => ({ ...p, sold: e.target.checked }))}
            className="h-4 w-4 accent-[#C85A32]"
          />
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-sand">
            Mark as sold — shows a tasteful Sold badge on the website
          </span>
        </label>

        <button
          data-testid="admin-artwork-save-button"
          onClick={save}
          disabled={busy}
          className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-umber py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] text-cream transition-colors hover:bg-vermilion disabled:opacity-60"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          {initial ? "Save changes" : "Add artwork"}
        </button>
      </div>
    </div>
  );
}

function ArtworksTab() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = () =>
    api
      .get("/artworks")
      .then(({ data }) => setItems(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));

  useEffect(() => {
    load();
  }, []);

  const del = async (a) => {
    if (!window.confirm(`Delete "${a.title}"?`)) return;
    try {
      await api.delete(`/artworks/${a.id}`);
      toast.success("Artwork deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (!items) return <div className="py-20 text-center font-mono text-xs uppercase tracking-widest text-sand">Loading…</div>;

  return (
    <div data-testid="admin-artworks-tab">
      <button
        data-testid="admin-artwork-add-button"
        onClick={() => setEditing("new")}
        className="flex items-center gap-2 rounded-full bg-umber px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-cream transition-colors hover:bg-vermilion"
      >
        <Plus className="h-4 w-4" /> Add artwork
      </button>

      <div className="mt-8 space-y-4">
        {items.map((a) => (
          <div key={a.id} data-testid={`admin-artwork-row-${a.id}`} className="flex items-center gap-5 border border-linen bg-white p-4">
            <img src={resolveImg(a.image)} alt={a.title} className="h-20 w-20 shrink-0 object-cover" />
            <div className="min-w-0 flex-1">
              <p className="truncate font-serif text-lg text-ink">
                {a.title}
                {a.sold && (
                  <span className="ml-2 inline-block rounded-full border border-gild/60 px-2 py-0.5 align-middle font-mono text-[9px] uppercase tracking-[0.18em] text-gild">
                    Sold
                  </span>
                )}
              </p>
              <p className="mt-0.5 truncate font-mono text-[10px] uppercase tracking-[0.18em] text-sand">
                {a.medium} · {a.size} · {a.year}
              </p>
              <p className="mt-0.5 font-serif text-sm text-vermilion">{a.price}</p>
            </div>
            <div className="flex shrink-0 gap-2">
              <button
                data-testid={`admin-artwork-edit-button-${a.id}`}
                onClick={() => setEditing(a)}
                className="rounded-full border border-linen p-2.5 text-clay transition-colors hover:border-gild hover:text-gild"
                aria-label={`Edit ${a.title}`}
              >
                <Pencil className="h-4 w-4" />
              </button>
              <button
                data-testid={`admin-artwork-delete-button-${a.id}`}
                onClick={() => del(a)}
                className="rounded-full border border-linen p-2.5 text-clay transition-colors hover:border-vermilion hover:text-vermilion"
                aria-label={`Delete ${a.title}`}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <ArtworkForm initial={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function ContentTab() {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get("/content")
      .then(({ data }) => setF({ ...DEFAULT_CONTENT, ...data, about_text: (data.about_paragraphs || DEFAULT_CONTENT.about_paragraphs).join("\n\n") }))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, []);

  if (!f) return <div className="py-20 text-center font-mono text-xs uppercase tracking-widest text-sand">Loading…</div>;

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/content", {
        tagline: f.tagline,
        whatsapp: f.whatsapp,
        email: f.email,
        instagram: f.instagram,
        whatsapp2: f.whatsapp2 || "",
        email2: f.email2 || "",
        instagram2: f.instagram2 || "",
        quote: f.quote,
        about_paragraphs: f.about_text.split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Site text updated — the website is live with your changes");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const field = "mt-1.5 w-full border border-linen bg-cream px-4 py-3 text-sm text-ink outline-none transition-colors focus:border-vermilion";
  const label = "font-mono text-[10px] uppercase tracking-[0.22em] text-sand";

  return (
    <div data-testid="admin-content-tab" className="max-w-xl space-y-5">
      <p className="text-sm text-clay">These details appear in the Get In Touch section of your website.</p>
      <label className="block">
        <span className={label}>WhatsApp number (digits with country code, e.g. 91XXXXXXXXXX)</span>
        <input data-testid="admin-content-whatsapp-input" value={f.whatsapp} onChange={set("whatsapp")} placeholder="91XXXXXXXXXX" className={field} />
      </label>
      <label className="block">
        <span className={label}>Email</span>
        <input data-testid="admin-content-email-input" value={f.email} onChange={set("email")} className={field} />
      </label>
      <label className="block">
        <span className={label}>Instagram handle</span>
        <input data-testid="admin-content-instagram-input" value={f.instagram} onChange={set("instagram")} className={field} />
      </label>
      <label className="block">
        <span className={label}>Salonee — WhatsApp number (digits with country code)</span>
        <input data-testid="admin-content-whatsapp2-input" value={f.whatsapp2 || ""} onChange={set("whatsapp2")} placeholder="91XXXXXXXXXX" className={field} />
      </label>
      <label className="block">
        <span className={label}>Salonee — Email</span>
        <input data-testid="admin-content-email2-input" value={f.email2 || ""} onChange={set("email2")} className={field} />
      </label>
      <label className="block">
        <span className={label}>Salonee — Instagram handle</span>
        <input data-testid="admin-content-instagram2-input" value={f.instagram2 || ""} onChange={set("instagram2")} className={field} />
      </label>
      <label className="block">
        <span className={label}>Quote</span>
        <textarea data-testid="admin-content-quote-input" rows={3} value={f.quote} onChange={set("quote")} className={field} />
      </label>
      <label className="block">
        <span className={label}>About text (blank line between paragraphs)</span>
        <textarea data-testid="admin-content-about-input" rows={8} value={f.about_text} onChange={set("about_text")} className={field} />
      </label>
      <button
        data-testid="admin-content-save-button"
        onClick={save}
        disabled={busy}
        className="flex items-center gap-2 rounded-full bg-umber px-8 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] text-cream transition-colors hover:bg-vermilion disabled:opacity-60"
      >
        {busy && <Loader2 className="h-4 w-4 animate-spin" />}
        Save site text
      </button>
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(undefined);
  const [tab, setTab] = useState("artworks");

  useEffect(() => {
    if (!getToken()) {
      setUser(null);
      return;
    }
    api
      .get("/auth/me")
      .then(({ data }) => setUser(data))
      .catch(() => {
        clearToken();
        setUser(null);
      });
  }, []);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {}
    clearToken();
    setUser(null);
    toast.success("Signed out");
  };

  if (user === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream">
        <Loader2 className="h-6 w-6 animate-spin text-vermilion" />
      </div>
    );
  }

  if (!user) return <LoginForm onLogin={setUser} />;

  return (
    <div className="min-h-screen bg-cream px-6 py-10 text-ink lg:px-10" data-testid="admin-panel">
      <div className="mx-auto max-w-3xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-vermilion">Owner Studio</p>
            <h1 className="mt-2 font-serif text-3xl tracking-tight">
              Artwith<span className="italic text-vermilion">.saivi</span>
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/"
              className="rounded-full border border-umber/25 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-ink transition-colors hover:border-vermilion hover:text-vermilion"
            >
              View site
            </a>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 rounded-full bg-umber px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-cream transition-colors hover:bg-vermilion"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>

        <div className="mt-10 flex gap-6 border-b border-linen">
          {[
            ["artworks", "Artworks"],
            ["content", "Site Text"],
          ].map(([id, label]) => (
            <button
              key={id}
              data-testid={`admin-tab-${id}`}
              onClick={() => setTab(id)}
              className={`-mb-px border-b-2 pb-3 font-mono text-[11px] uppercase tracking-[0.22em] transition-colors ${
                tab === id ? "border-vermilion text-vermilion" : "border-transparent text-sand hover:text-ink"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="py-10">
          {tab === "artworks" ? <ArtworksTab /> : <ContentTab />}
        </div>
      </div>
    </div>
  );
}
